import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';
import bandtangoLogo from './bandtango_scaled.png';
import './App.css';
import './index.css';
import { apiUrl } from './config';

function InvoicePaymentResultForm() {
	const navigate = useNavigate();
	const location = useLocation();
	const isSuccess = location.pathname.includes('/payment/success');
	const isNext = location.pathname.includes('/payment/next');
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');
	const [invoice, setInvoice] = useState(null);
	const [customer, setCustomer] = useState(null);

	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const invoiceId = searchParams.get('invoice_id') || searchParams.get('invoiceId') || '';
	const statusLabel = isSuccess ? 'Payment Successful' : 'Payment Processing';
	const statusMessage = isSuccess
		? 'Your payment was submitted successfully. Your business is very important to us.'
		: 'Your payment is being processed. We will update this page once the result is available.';

	const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(Number(value || 0));

	const formatDate = (value) => {
		if (!value) return '—';
		try {
			return new Date(value).toLocaleDateString();
		} catch {
			return String(value);
		}
	};

	const invoiceBalance = useMemo(() => {
		const amountDue = Number(invoice?.amount_due ?? 0);
		const totalValue = Number(invoice?.total_value ?? 0);
		return amountDue > 0 ? amountDue : totalValue;
	}, [invoice]);

	useEffect(() => {
		const loadResultInvoice = async () => {
			setIsLoading(true);
			setError('');

			if (!invoiceId) {
				setError('Missing invoice id.');
				setIsLoading(false);
				return;
			}

			try {
				const invoiceEndpoints = [
					`/invoice/one/${invoiceId}`,
					`/invoice/byid/${invoiceId}`,
					`/invoice/${invoiceId}`,
				];

				let loadedInvoice = null;
				for (const endpoint of invoiceEndpoints) {
					const response = await fetch(apiUrl(endpoint), {
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
						},
					});

					if (!response.ok) {
						if (response.status === 404) {
							continue;
						}
						throw new Error(`Failed to load invoice: ${response.status} ${response.statusText}`);
					}

					const body = await response.json();
					loadedInvoice = body?.data || body;
					if (loadedInvoice) break;
				}

				if (!loadedInvoice) {
					throw new Error('Invoice not found');
				}

				setInvoice(loadedInvoice);

				const customerId = loadedInvoice.customer_id;
				if (customerId) {
					const customerResponse = await fetch(apiUrl(`/customer/one?customerId=${encodeURIComponent(customerId)}`), {
						method: 'GET',
						headers: { 'Content-Type': 'application/json' },
					});
					if (customerResponse.ok) {
						const loadedCustomer = await customerResponse.json();
						setCustomer(loadedCustomer);
					}
				}
			} catch (loadError) {
				console.error('Error loading payment result details:', loadError);
				setError(loadError.message || 'Failed to load payment result');
			} finally {
				setIsLoading(false);
			}
		};

		loadResultInvoice();
	}, [invoiceId]);

	if (isLoading) {
		return (
			<div className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-4xl rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
					<div className="text-center text-slate-500">Loading payment result...</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-5xl space-y-6">
				<div className="overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 shadow-xl">
					<div className="flex items-center justify-center border-b border-white/20 bg-slate-700 px-8 py-6">
						<img src={bandtangoLogo} alt="Bandtango" className="h-16 w-auto object-contain" />
					</div>
					<div className="flex flex-col gap-6 px-6 py-8 text-white sm:px-10 lg:flex-row lg:items-center lg:justify-between">
						<div className="max-w-2xl">
							<div className="flex items-center gap-3">
								{isSuccess ? (
									<CheckCircleIcon className="h-8 w-8 text-emerald-200" aria-hidden="true" />
								) : (
									<ClockIcon className="h-8 w-8 text-yellow-200" aria-hidden="true" />
								)}
								<div>
									<p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-100">Invoice Payment</p>
									<h1 className="mt-2 text-3xl font-bold sm:text-4xl">{statusLabel}</h1>
								</div>
							</div>
							<p className="mt-4 text-sm text-indigo-50 sm:text-base">{statusMessage}</p>
						</div>
						<div className="rounded-2xl bg-white/15 px-6 py-4 text-right backdrop-blur">
							<div className="text-xs uppercase tracking-wide text-indigo-100">Invoice</div>
							<div className="mt-1 text-xl font-bold text-white">
								{invoice?.due_date
									? new Date(invoice.due_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
									: invoice?.name || invoice?.order_campaign || invoiceId}
							</div>
							<div className="mt-1 text-sm text-indigo-100">{formatCurrency(invoiceBalance)}</div>
						</div>
					</div>
				</div>

				{error && (
					<div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
						{error}
					</div>
				)}

				<div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
					<section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
						<h2 className="text-xl font-semibold text-slate-900">Invoice Summary</h2>
						<div className="mt-6 grid gap-5 sm:grid-cols-2">
							<div className="rounded-2xl bg-slate-50 p-4">
								<div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bill To</div>
								<div className="mt-2 text-base font-semibold text-slate-900">{customer?.billing_name || customer?.name || invoice?.name || 'Customer'}</div>
								<div className="mt-2 space-y-1 text-sm text-slate-600">
									{[
										customer?.billing_address_1 || customer?.address_1,
										customer?.billing_address_2 || customer?.address_2,
										[
											customer?.billing_city || customer?.city,
											customer?.billing_state || customer?.state,
											customer?.billing_zip_code || customer?.zip_code,
										].filter(Boolean).join(' '),
									].filter(Boolean).map((line) => (
										<div key={line}>{line}</div>
									))}
									<div>{customer?.billing_contact_email || customer?.contact_email || '—'}</div>
									<div>{customer?.billing_contact_phone || customer?.contact_phone || customer?.telephone || '—'}</div>
								</div>
							</div>
							<div className="rounded-2xl bg-slate-50 p-4">
								<div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice Details</div>
								<dl className="mt-3 space-y-2 text-sm text-slate-700">
									<div className="flex justify-between gap-4"><dt>Due Date</dt><dd className="font-medium text-slate-900">{formatDate(invoice?.due_date)}</dd></div>
									<div className="flex justify-between gap-4"><dt>Total Value</dt><dd className="font-medium text-slate-900">{formatCurrency(invoice?.total_value)}</dd></div>
									<div className="flex justify-between gap-4"><dt>Amount Due</dt><dd className="font-medium text-emerald-700">{formatCurrency(invoiceBalance)}</dd></div>
								</dl>
							</div>
						</div>

						<div className="mt-6 overflow-hidden rounded-2xl ring-1 ring-slate-200">
							<div className="bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Payment Result</div>
							<div className="bg-white px-4 py-6 text-sm text-slate-700">
								<p>Invoice ID: {invoiceId}</p>
								<p className="mt-2">Status: {isSuccess ? 'Success' : 'Pending / Next Step'}</p>
							</div>
						</div>
					</section>

					<section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
						<div className="space-y-4">
							<h2 className="text-xl font-semibold text-slate-900">Thank You for your payment!</h2>
							<p className="text-sm text-slate-500">Your payment supports our mission of making music fair again.  Helping artists, entertainers, and content creators earn their fair share.</p>
						</div>
					</section>
				</div>
			</div>
		</div>
	);
}

export default InvoicePaymentResultForm;
